import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import supabase from '../supabase/supabase';

const UserDataContext = createContext();

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within UserDataProvider');
  }
  return context;
};

export const UserDataProvider = ({ children }) => {
  const { user } = useAuth();
  const [isNewUser, setIsNewUser] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [loadingOnboarding, setLoadingOnboarding] = useState(true);

  useEffect(() => {
    if (user) {
      checkUserStatus();
    } else {
      setLoadingOnboarding(false);
    }
  }, [user]);

  const checkUserStatus = async () => {
    try {
      setLoadingOnboarding(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      const completed = data?.onboarding_completed === true;
      setHasCompletedOnboarding(completed);
      setIsNewUser(!completed);
    } catch (error) {
      console.error('Error checking user status:', error);
      setHasCompletedOnboarding(false);
      setIsNewUser(true);
    } finally {
      setLoadingOnboarding(false);
    }
  };

  const completeOnboarding = async () => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ onboarding_completed: true, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);

      if (error) throw error;
      setHasCompletedOnboarding(true);
      setIsNewUser(false);
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  const getUserData = (dataType) => {
    if (!user) return null;
    const userKey = `athletepath_user_${user.id}`;
    const userData = localStorage.getItem(userKey);
    if (!userData) return null;
    const parsed = JSON.parse(userData);
    return dataType ? parsed[dataType] : parsed;
  };

  const updateUserData = (dataType, data) => {
    if (!user) return;
    const userKey = `athletepath_user_${user.id}`;
    const userData = localStorage.getItem(userKey);
    let parsed = userData ? JSON.parse(userData) : {};
    parsed[dataType] = data;
    parsed.updatedAt = new Date().toISOString();
    localStorage.setItem(userKey, JSON.stringify(parsed));
  };

  return (
    <UserDataContext.Provider
      value={{
        isNewUser,
        hasCompletedOnboarding,
        loadingOnboarding,
        completeOnboarding,
        getUserData,
        updateUserData,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};